import { Font } from '@react-pdf/renderer'

// ─── Primitive color palette ────────────────────────────────────────────────
// Literal hex values from @radix-ui/colors (light mode only — PDFs are always
// rendered in light mode). Source: node_modules/@radix-ui/colors/*.css
//
// To retheme the entire palette: update the hex values here.
// Semantic tokens below inherit automatically.

const GRAY = {
  1:  '#fcfcfc',
  2:  '#f9f9f9',
  3:  '#f0f0f0',
  4:  '#e8e8e8',
  5:  '#e0e0e0',
  6:  '#d9d9d9',
  7:  '#cecece',
  8:  '#bbbbbb',
  9:  '#8d8d8d',
  10: '#838383',
  11: '#646464',
  12: '#202020',
} as const

const BLUE = {
  9:  '#0090ff',
  11: '#0d74ce',
} as const

const VIOLET = {
  4:  '#ebe4ff',
  9:  '#6e56cf',
  11: '#6550b9',
} as const

const AMBER = {
  2:  '#fefbe9',
  6:  '#f3d673',
  11: '#ab6400',
} as const

const RED = {
  2:  '#fff7f7',
  6:  '#fdbdbe',
  11: '#ce2c31',
} as const

const JADE = {
  9:  '#29a383',
  11: '#208368',
} as const

// ─── Brand accent ────────────────────────────────────────────────────────────
// Single value that drives bars, header rules, and branded elements.
// To retheme: change this one value.
export const BRAND_ACCENT = '#006eb8'

// ─── Semantic color tokens ───────────────────────────────────────────────────
// Named tokens that mirror the UI design system. Use these everywhere in PDF
// components — never reference the raw palette directly in document code.
export const colors = {
  // Backgrounds
  canvas:        GRAY[1],
  surface:       GRAY[2],
  fill:          GRAY[3],

  // Borders
  lineSubtle:    GRAY[6],
  line:          GRAY[7],
  lineStrong:    GRAY[8],

  // Text
  fg:            GRAY[12],
  fgSecondary:   GRAY[11],
  fgMuted:       GRAY[9],

  // Controls
  solid:         GRAY[9],

  // Brand
  accent:        BRAND_ACCENT,
  accentText:    BLUE[11],

  // Accents (for decorative use)
  violetSubtle:  VIOLET[4],
  violetSolid:   VIOLET[9],

  // Status — warning (amber)
  warningFill:   AMBER[2],
  warningLine:   AMBER[6],
  warningFg:     AMBER[11],

  // Status — error (red)
  errorFill:     RED[2],
  errorLine:     RED[6],
  errorFg:       RED[11],

  // Status — success (jade)
  successFg:     JADE[11],

  white:         '#ffffff',
} as const

// ─── Font families ───────────────────────────────────────────────────────────
// Names must match exactly what is registered with Font.register() below.
// To swap fonts: update both the name here and the src path in registerFonts().
export const fonts = {
  sans:  'Inter',          // UI / metadata text
  serif: 'Zodiak',         // Headlines, body prose
  mono:  'Courier',        // Built-in PDF monospace — no registration needed
} as const

// ─── Typography scale (pt) ───────────────────────────────────────────────────
// 1pt = 1/72 inch. Standard print body text is 10–11pt.
// To retheme: adjust sizes here.
export const type = {
  size: {
    '2xs': 6,
    xs:    7.5,
    sm:    8.5,
    base:  10,
    md:    11,
    lg:    14,
    xl:    18,
    '2xl': 24,
    '3xl': 30,
  },
  leading: {
    tight:   1.2,
    snug:    1.35,
    normal:  1.5,
    relaxed: 1.65,
  },
  weight: {
    light:  300,
    normal: 400,
    bold:   700,
  },
} as const

// ─── Spacing scale (pt) ──────────────────────────────────────────────────────
// Used for margins, padding, and gaps between sections.
export const space = {
  1:  4,
  2:  8,
  3:  12,
  4:  16,
  5:  20,
  6:  24,
  8:  32,
  10: 40,
  12: 48,
} as const

// ─── Page geometry (pt) ──────────────────────────────────────────────────────
// A4 = 595 × 842 pt. Content width = 595 − 48 − 48 = 499 pt.
export const pageGeometry = {
  size:         'A4' as const,
  marginTop:    36,
  marginBottom: 60,  // extra room for fixed footer
  marginLeft:   48,
  marginRight:  48,
} as const

// ─── Brand identity ──────────────────────────────────────────────────────────
// Configurable strings for document headers.
// To retheme: change these values.
export const brand = {
  name:        'WIRe',
  tagline:     'Intelligence Platform',
} as const

// ─── Font registration ───────────────────────────────────────────────────────
// Call once before rendering any PDF. Idempotent — safe to call multiple times.
//
// Fonts are fetched in the browser via the URLs below. They must be accessible
// at runtime (served as static assets from /public/fonts/).
//
// @react-pdf/renderer v4 supports woff2 natively. No conversion needed.
//
// To add or replace a font:
//   1. Drop the file into public/fonts/<family>/
//   2. Update the src path and fontWeight below.
//   3. Update the fonts.* constant above if the family name changes.

let _fontsRegistered = false

export function registerFonts(): void {
  if (_fontsRegistered) return
  _fontsRegistered = true

  // Inter Variable — one file covers weights 100–900.
  // We register the same file for 400 and 700 so @react-pdf resolves both
  // weight requests without synthesizing a faux-bold.
  Font.register({
    family: fonts.sans,
    fonts: [
      { src: '/fonts/inter/inter-latin-wght-normal.ttf', fontWeight: 400, fontStyle: 'normal' },
      { src: '/fonts/inter/inter-latin-wght-normal.ttf', fontWeight: 400, fontStyle: 'italic' },
      { src: '/fonts/inter/inter-latin-wght-normal.ttf', fontWeight: 700, fontStyle: 'normal' },
      { src: '/fonts/inter/inter-latin-wght-normal.ttf', fontWeight: 700, fontStyle: 'italic' },
    ],
  })

  // Zodiak — separate files per weight.
  // Each weight is registered for both normal and italic so that
  // fontStyle: 'italic' resolves without error (react-pdf applies a
  // faux skew when no dedicated italic file is available).
  Font.register({
    family: fonts.serif,
    fonts: [
      { src: '/fonts/zodiak/Zodiak-Light.ttf',   fontWeight: 300, fontStyle: 'normal' },
      { src: '/fonts/zodiak/Zodiak-Light.ttf',   fontWeight: 300, fontStyle: 'italic' },
      { src: '/fonts/zodiak/Zodiak-Regular.ttf', fontWeight: 400, fontStyle: 'normal' },
      { src: '/fonts/zodiak/Zodiak-Regular.ttf', fontWeight: 400, fontStyle: 'italic' },
      { src: '/fonts/zodiak/Zodiak-Bold.ttf',    fontWeight: 700, fontStyle: 'normal' },
      { src: '/fonts/zodiak/Zodiak-Bold.ttf',    fontWeight: 700, fontStyle: 'italic' },
    ],
  })

  // Disable automatic hyphenation — professional editorial documents
  // should control line breaks explicitly.
  Font.registerHyphenationCallback((word) => [word])
}
